export const downloadSummativeMarksheetPdf = async ({
  institution,
  facultyName,
  departmentName,
  programName,
  unitName,
  unitCode,
  semesterName,
  academicYear,
  students = [],
}) => {
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageWidth = 297;
  const pageHeight = 210;
  const centerX = pageWidth / 2;
  const leftMargin = 15;
  const rightMargin = 282;
  const tableWidth = rightMargin - leftMargin;
  const columns = [
    { label: 'Serial No#', width: 16, align: 'center' },
    { label: 'Name Of Student', width: 49, align: 'left' },
    { label: 'REG NO', width: 34, align: 'left' },
    { label: 'FA (/60)', width: 22, align: 'center' },
    { label: 'ATTENDANCE (%)', width: 25, align: 'center' },
    { label: 'SUMMATIVE (/100)', width: 29, align: 'center' },
    { label: 'CONVERTED (/40)', width: 28, align: 'center' },
    { label: 'GRADE', width: 20, align: 'center' },
    { label: 'RATING', width: 16, align: 'center' },
    { label: 'STATUS', width: 28, align: 'center' },
  ];

  const columnX = columns.reduce((positions, column, index) => {
    positions.push(index === 0 ? leftMargin : positions[index - 1] + columns[index - 1].width);
    return positions;
  }, []);
  const lastColumn = columns.length - 1;

  const addHeader = async () => {
    let y = 14;
    if (institution?.logo_url) {
      try {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.src = institution.logo_url;
        await awaitImage(image);
        if (image.width && image.height) {
          const height = 16;
          const width = (image.width / image.height) * height;
          pdf.addImage(image, 'PNG', centerX - width / 2, y, width, height);
          y += height + 6;
        }
      } catch (_) {
        // A missing logo must not prevent marksheet generation.
      }
    }

    pdf.setFont('times', 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(13);
    pdf.text((institution?.name || 'INSTITUTION').toUpperCase(), centerX, y, { align: 'center' });
    y += 6;
    if (facultyName) {
      pdf.setFontSize(11);
      pdf.text(facultyName.toUpperCase(), centerX, y, { align: 'center' });
      y += 6;
    }
    if (departmentName) {
      pdf.setFontSize(10.5);
      pdf.text(departmentName.toUpperCase(), centerX, y, { align: 'center' });
      y += 5;
    }

    pdf.setFontSize(11);
    const title = 'SUMMATIVE EXAMINATION MARKSHEET';
    pdf.text(title, centerX, y, { align: 'center' });
    const titleWidth = pdf.getTextWidth(title);
    pdf.line(centerX - titleWidth / 2, y + 0.8, centerX + titleWidth / 2, y + 0.8);
    y += 5;

    const semesterHeading = semesterName
      ? `${String(semesterName).toUpperCase().replace(/SEMESTER/i, '').trim()} SEMESTER EXAMINATIONS`
      : 'SEMESTER EXAMINATIONS';
    pdf.setFontSize(10.5);
    pdf.text(semesterHeading, centerX, y, { align: 'center' });
    const semesterWidth = pdf.getTextWidth(semesterHeading);
    pdf.line(centerX - semesterWidth / 2, y + 0.8, centerX + semesterWidth / 2, y + 0.8);
    y += 5;
    if (academicYear) {
      pdf.text(String(academicYear), centerX, y, { align: 'center' });
      const yearWidth = pdf.getTextWidth(String(academicYear));
      pdf.line(centerX - yearWidth / 2, y + 0.8, centerX + yearWidth / 2, y + 0.8);
      y += 5;
    }
    if (programName) {
      pdf.text(String(programName).toUpperCase(), centerX, y, { align: 'center' });
      y += 5;
    }
    pdf.text(`${unitName ? `${unitName} - ` : ''}${unitCode || ''}`.toUpperCase(), centerX, y, { align: 'center' });
    y += 4;
    pdf.setLineWidth(0.5);
    pdf.line(leftMargin, y, rightMargin, y);
    return y + 4;
  };

  const addTableHeader = (y) => {
    const headerHeight = 8;
    pdf.setFillColor(235, 235, 235);
    pdf.rect(leftMargin, y, tableWidth, headerHeight, 'F');
    pdf.setLineWidth(0.6);
    pdf.line(leftMargin, y, rightMargin, y);
    pdf.line(leftMargin, y + headerHeight, rightMargin, y + headerHeight);
    pdf.setFont('times', 'bold');
    pdf.setFontSize(9.5);
    columns.forEach((column, index) => {
      const x = column.align === 'center' ? columnX[index] + column.width / 2 : columnX[index] + 3;
      pdf.text(column.label, x, y + 5.5, { align: column.align });
    });
    return y + headerHeight;
  };

  let y = await addHeader();
  y = addTableHeader(y);
  const statusX = columnX[lastColumn] + columns[lastColumn].width / 2;
  const rowHeight = 6.5;
  pdf.setFont('times', 'normal');
  pdf.setFontSize(9);

  if (!students.length) {
    pdf.text('No paid summative examination bookings found.', leftMargin + tableWidth / 2, y + 5, { align: 'center' });
    y += rowHeight;
  } else {
    students.forEach((student, index) => {
      if (y + rowHeight > pageHeight - 35) {
        pdf.addPage('a4', 'landscape');
        y = addTableHeader(15);
      }
      const raw = student.raw_marks === '' || student.raw_marks === null || student.raw_marks === undefined
        ? '--'
        : Number(student.raw_marks).toFixed(2);
      const converted = student.converted_marks === '' || student.converted_marks === null || student.converted_marks === undefined
        ? '--'
        : Math.round(Number(student.converted_marks));
      const formative = Math.round(Number(student.formative_total_60 || 0));
      const attendance = student.attendance_recorded
        ? `${Number(student.attendance_percentage || 0).toFixed(2)}%`
        : '--';
      const values = [
        String(index + 1),
        student.student_name || 'Unknown student',
        student.admission_number || '—',
        String(formative),
        attendance,
        raw,
        converted,
        student.grade_letter || '--',
        student.rating || '--',
      ];
      columns.slice(0, lastColumn).forEach((column, columnIndex) => {
        const x = column.align === 'center'
          ? columnX[columnIndex] + column.width / 2
          : columnX[columnIndex] + 3;
        pdf.text(values[columnIndex], x, y + 4.5, { align: column.align });
      });
      pdf.setFont('times', 'bold');
      const status = !student.attendance_recorded
        ? 'Attendance pending'
        : Number(student.attendance_percentage) < 70
          ? 'Below 70%'
          : student.raw_marks === null || student.raw_marks === undefined || student.raw_marks === ''
            ? 'Mark pending'
            : 'Marked';
      pdf.text(status, statusX, y + 4.5, { align: 'center', maxWidth: columns[lastColumn].width - 1 });
      pdf.setFont('times', 'normal');
      pdf.setLineWidth(index === students.length - 1 ? 0.6 : 0.3);
      pdf.setLineDash(index === students.length - 1 ? [] : [0.6, 0.8], 0);
      pdf.line(leftMargin, y + rowHeight, rightMargin, y + rowHeight);
      pdf.setLineDash([], 0);
      y += rowHeight;
    });
  }

  if (y + 48 > pageHeight) {
    pdf.addPage('a4', 'landscape');
    y = 18;
  } else {
    y += 8;
  }
  pdf.setFont('times', 'bold');
  pdf.setFontSize(9);
  pdf.text('KEY:', leftMargin, y);
  pdf.setFont('times', 'normal');
  pdf.text('-- : Missing attendance or summative marks', leftMargin + 10, y);
  pdf.text('Attendance threshold: 70%', leftMargin + 86, y);
  pdf.text('FA mark is out of 60', leftMargin + 152, y);
  pdf.text('Converted mark is out of 40', leftMargin + 212, y);
  y += 12;

  pdf.setFont('times', 'bold');
  pdf.setFontSize(9.5);
  const sigWidth = 70;
  const sigGap = 20;
  const sigStartX = leftMargin + 10;
  [['TRAINER / LECTURER:', sigStartX], ['HEAD OF DEPARTMENT:', sigStartX + sigWidth + sigGap], ['EXTERNAL VERIFIER:', sigStartX + (sigWidth + sigGap) * 2]].forEach(([label, x]) => {
    pdf.text(label, x, y);
    pdf.line(x, y + 10, x + sigWidth, y + 10);
    pdf.setFont('times', 'normal');
    pdf.setFontSize(8.5);
    pdf.text('Name, Signature & Date', x, y + 14);
    pdf.setFont('times', 'bold');
    pdf.setFontSize(9.5);
  });

  const safeUnitCode = (unitCode || 'Unit').replace(/[^a-zA-Z0-9_-]/g, '_');
  pdf.save(`Summative_Marksheet_${safeUnitCode}.pdf`);
};

const awaitImage = image => new Promise(resolve => {
  if (image.complete) {
    resolve();
    return;
  }
  image.onload = resolve;
  image.onerror = resolve;
});
